import React, { useEffect } from 'react';
import { Button, TextField } from '@cimpress/react-components';
import { Template, TruckFormData, TruckFormType, Truck } from '../../models/truck';
import { LoadingRestriction } from '../../models/loading-restriction';
import { LocationKey, Options, Option } from '../../models/utils';
import { valueToArray } from '../../utils/functions';
import { Select } from '../../components/Select';
import { FlexBox, JustifyContent } from '../../utils/css-classes';

interface TruckFormProps {
  submit: (truck: TruckFormData) => void;
  hide: () => void;
  linehaulCompanies: Options;
  customsBrokers: Options;
  truckLoadingRestrictions: { [k: string]: { name: string; all: Options; } };
  selectedLocationKey: LocationKey;
  templates?: Template[];
}

type NewTruckFormProps = TruckFormProps & {
  type: TruckFormType.ADD;
};

type OtherTruckFormProps = TruckFormProps & {
  truck: Truck;
  type: Exclude<TruckFormType, TruckFormType.ADD>;
};

const groupLoadingRestrictions = function groupLoadingRestrictions(loadingRestrictions: LoadingRestriction[]) {
  const categories: { [k: string]: { label: string; value: string; }[]; } = {};
  for (const { restrictionType, displayValue: label, value } of loadingRestrictions) {
    if (!categories[restrictionType]) {
      categories[restrictionType] = [];
    }
    categories[restrictionType].push({ label, value });
  }
  return categories;
};

// tslint:disable-next-line:variable-name
const TruckFormComponent = (props: NewTruckFormProps | OtherTruckFormProps) => {
  const {
    submit,
    hide,
    type,
    linehaulCompanies,
    customsBrokers,
    truckLoadingRestrictions,
    selectedLocationKey,
    templates,
  } = props;

  const templateOptions = (templates || [])
    .reduce(
      (acc: Options, { name }, i) => [...acc, { label: name, value: `${i}` }],
      [],
    )
    .sort((templateA, templateB) => templateA.label.localeCompare(templateB.label));

  const truck = type === TruckFormType.ADD ? null : (props as OtherTruckFormProps).truck;

  const matchCustomsBroker = (customsBroker: Option) => {
    if (!truck) {
      return false;
    }
    return !!truck.manifest
      && !!truck.manifest.options.customsBroker
      && truck.manifest.options.customsBroker.id === customsBroker.value
      || truck.customsBrokerId === customsBroker.value;
  };

  const id = truck ? truck.id : null;

  const [name, setName] = React.useState(truck ? truck.name : '');
  const [crossesBorder, setCrossesBorderInternal] = React.useState(truck ? truck.crossesBorder : false);
  const [linehaulCompany, setLinehaulCompany] =
    React.useState(linehaulCompanies.find(lc => !!truck && lc.value === truck.linehaulCompanyId) || null);
  const [customsBroker, setCustomsBroker] =
    React.useState(customsBrokers.find(matchCustomsBroker));
  const [loadingRestrictions, setLoadingRestrictions] =
    React.useState(truck ? groupLoadingRestrictions(truck.loadingRestrictions || []) : {});

  const [template, setTemplate] = React.useState<Option>();

  const setCrossesBorder = (value: boolean) => {
    setCrossesBorderInternal(value);
    if (!value) {
      setCustomsBroker(undefined);
    }
  };

  useEffect(
    () => {
      setTemplate(undefined);
      setName(truck ? truck.name : '');
      setLinehaulCompany(linehaulCompanies.find(lc => !!truck && lc.value === truck.linehaulCompanyId) || null);
      setCustomsBroker(customsBrokers.find(matchCustomsBroker));
      setCrossesBorder(truck ? truck.crossesBorder : false);
      setLoadingRestrictions(truck ? groupLoadingRestrictions(truck.loadingRestrictions || []) : {});
    },
    [truck],
  );

  useEffect(
    () => {
      if (templates && template) {
        const currentTemplate = templates[parseInt(template.value, 10)];
        const {
          crossesBorder,
          truckName,
          linehaulCompanyId,
          loadingRestrictions,
          customsBrokerId,
        } = currentTemplate;
        setCrossesBorder(crossesBorder);
        setName(truckName || '');
        setCustomsBroker(customsBrokers.find(cb => cb.value === customsBrokerId));
        setLinehaulCompany(linehaulCompanies.find(lc => lc.value === linehaulCompanyId) || null);
        setLoadingRestrictions(
          Object.entries(truckLoadingRestrictions).reduce(
            (acc, [restrictionType, restrictions]) => ({
              ...acc,
              [restrictionType]: restrictions.all.filter(
                restriction => (loadingRestrictions || []).findIndex(
                  templateRestriction =>
                    templateRestriction.restrictionType === restrictionType
                    && templateRestriction.value === restriction.value,
                ) >= 0,
              ),
            }),
            {},
          ),
        );
      }
    },
    [template],
  );

  const handleSubmit = (event: any) => {
    event && event.preventDefault();
    const formData: TruckFormData = {
      id,
      crossesBorder,
      loadingRestrictions:
        Object.entries(loadingRestrictions).reduce(
          (all: LoadingRestriction[], [restrictionType, cur]) => [
            ...all,
            ...cur.map(({ label, value }) => ({ restrictionType, value, displayValue: label })),
          ],
          [],
        ),
      name: name.trim(),
      linehaulCompanyId: linehaulCompany ? linehaulCompany.value : null,
      customsBrokerId: customsBroker ? customsBroker.value : null,
      locationKey: selectedLocationKey,
    };

    if (formData.name) {
      submit(formData);
      hide();
    }
  };

  const truckLoadingRestrictionsSelect =
    Object.keys(truckLoadingRestrictions)
      .map(key =>
        <Select
          searchable
          clearable
          isMulti
          tether
          key={key}
          className="max-width-375"
          isDisabled={
            type === TruckFormType.MANIFEST ||
            type === TruckFormType.SHOW
          }
          label={`Select ${truckLoadingRestrictions[key].name}`}
          value={loadingRestrictions[key]}
          options={truckLoadingRestrictions[key].all}
          onChange={options => setLoadingRestrictions({
            ...loadingRestrictions,
            [key]: valueToArray(options),
          })}
        />,
      );

  const linehaulCompanySelect = (
    <Select
      tether
      isDisabled={
        type === TruckFormType.MANIFEST ||
        type === TruckFormType.SHOW
      }
      label="Select a line haul company"
      value={linehaulCompany || linehaulCompanies[0]}
      options={linehaulCompanies}
      required={crossesBorder}
      onChange={option => setLinehaulCompany(valueToArray(option)[0])}
    />
  );

  const crossesBorderCheckBox = (
    <div className="mb-4 checkbox">
      <label>
        <input
          disabled={
            type === TruckFormType.MANIFEST ||
            type === TruckFormType.SHOW
          }
          type="checkbox"
          checked={crossesBorder}
          onChange={event => setCrossesBorder(event.target.checked)}
        />
        Truck will be crossing an international border
      </label>
    </div>
  );

  const customsBrokerSelect = (
    <Select
      tether
      autoFocus={type === TruckFormType.MANIFEST && truck!.crossesBorder}
      isDisabled={type === TruckFormType.SHOW}
      label="Select a customs broker"
      value={customsBroker}
      options={customsBrokers}
      onChange={option => setCustomsBroker(valueToArray(option)[0])}
    />
  );

  const requiresCustomsBroker = (type === TruckFormType.MANIFEST) && crossesBorder && customsBrokers.length > 0;
  const requiresLinehaulCompany = (type === TruckFormType.MANIFEST) && crossesBorder && linehaulCompanies.length > 1;
  const disableSubmitButton = !name.trim().length
    || (requiresLinehaulCompany && (!linehaulCompany || !linehaulCompany!.value))
    || (requiresCustomsBroker && (!customsBroker || !customsBroker!.value));

  return (
    <div className={`m-2 ${FlexBox.FLEX} ${JustifyContent.CENTER}`}>
      <form onSubmit={(event) => handleSubmit(event)}>
        <fieldset>
          {
            templates &&
            <Select
              label="Load template"
              labelKey="name"
              options={templateOptions}
              value={template}
              onChange={option => setTemplate(valueToArray(option)[0])}
              clearable={false}
            />
          }
          <TextField
            disabled={type === TruckFormType.MANIFEST || type === TruckFormType.SHOW}
            // inputRef={(inputRef) => setNameField(inputRef as any)}
            label="Truck name"
            value={name}
            autoFocus={type !== TruckFormType.MANIFEST}
            onChange={event => setName(event.target.value)}
          />
          {linehaulCompanies.length > 1 && linehaulCompanySelect}
          {truckLoadingRestrictionsSelect}
          {customsBrokers.length > 0 && crossesBorderCheckBox}
          {crossesBorder && customsBrokerSelect}
          {
            type !== TruckFormType.SHOW &&
            <div className={`pt-4 ${FlexBox.FLEX} ${JustifyContent.SPACE_BETWEEN}`}>
              <input type="submit" hidden />
              <Button
                className="mr-4 min-width-180"
                type="default"
                onClick={hide}
              >
                Cancel
              </Button>
              <Button
                className="min-width-180"
                type="primary"
                onClick={handleSubmit}
                disabled={disableSubmitButton}
              >
                {type === TruckFormType.MANIFEST ? 'Generate Manifest' : 'Done'}
              </Button>
            </div>
          }
        </fieldset>
      </form>
    </div>
  );
};

export default TruckFormComponent;
